import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Pagination,
  Dialog,
  DialogContent,
  IconButton,
  Avatar,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ImageIcon from '@mui/icons-material/Image'; // Added ImageIcon for fallback
import KenLoader from '../../../../GlobalComponents/KenLoader';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const ITEMS_PER_PAGE = 20;

// Define types for our data
interface FileData {
  Id: string;
  Name: string;
  Description__c: string;
  Location__c: string;
  File_Link__c: string;
  File_Admin__r: {
    Id: string;
    Profile_Picture_URL__c: string;
    Name: string
  };
}

interface PhotoContainerProps {
  cardData: FileData[];
}

const PhotoContainer: React.FC<PhotoContainerProps> = ({ cardData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedData, setPaginatedData] = useState<FileData[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setLoading(true);

    const timer = setTimeout(() => {
      if (cardData?.length) {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        setPaginatedData(cardData.slice(startIndex, endIndex));
        setTotalPages(Math.ceil(cardData.length / ITEMS_PER_PAGE));
      } else {
        setPaginatedData([]);
        setTotalPages(1);
      }
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [cardData, currentPage]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleOpenDialog = (item: FileData) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
  };

  const isValidImageUrl = (url: string | null | undefined) => {
    return url && url.trim() !== '';
  };

  return (
    <Box width="100%" display="flex" justifyContent="center" alignItems="center" minHeight="200px">
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="400px">
          <KenLoader />
        </Box>
      ) : (
        <Paper sx={{
          borderRadius: 3,
          padding: { xs: 1, sm: 2 },
          boxShadow: 'none',
          backgroundColor: 'transparent',
          width: '100%'
        }}>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {paginatedData.map((item) => (
              <Grid item xs={6} sm={6} md={4} lg={3} key={item.Id}>
                <Paper
                  elevation={1}
                  sx={{
                    borderRadius: { xs: 2, sm: 3 },
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    },
                    position: 'relative',
                    height: { xs: 150, sm: 180, md: 220 },
                  }}
                  onClick={() => handleOpenDialog(item)}
                >
                  {isValidImageUrl(item.File_Link__c) ? (
                    <Box
                      component="img"
                      src={item.File_Link__c}
                      alt={item.Name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f0f0f0',
                      }}
                    >
                      <ImageIcon
                        sx={{
                          fontSize: { xs: 40, sm: 60, md: 80 },
                          color: '#bdbdbd'
                        }}
                      />
                    </Box>
                  )}

                  <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    display="flex"
                    alignItems="center"
                    p={{ xs: 1, sm: 2 }}
                    width="100%"
                    sx={{
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                    }}
                  >
                    <Avatar
                      src={item.File_Admin__r?.Profile_Picture_URL__c}
                      alt={item.Name}
                      sx={{
                        width: { xs: 24, sm: 30, md: 36 },
                        height: { xs: 24, sm: 30, md: 36 },
                        marginRight: 1
                      }}
                    />
                    <Typography
                      fontWeight={500}
                      color="white"
                      fontSize={{ xs: 12, sm: 13, md: 14 }}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {item.File_Admin__r?.Name}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
          {totalPages > 1 && (
            <Box
              display="flex"
              justifyContent="center"
              mt={{ xs: 2, sm: 3 }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                variant="outlined"
                shape="rounded"
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={isMobile ? 0 : 1}
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    fontWeight: 400,
                    color: '#000',
                    border: '1px solid #E0E0E0',
                    '&:hover': {
                      backgroundColor: '#F5F5F5',
                    },
                  },
                  '& .MuiPaginationItem-page.Mui-selected': {
                    backgroundColor: '#123A6F',
                    color: '#fff',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#123A6F',
                    },
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      )}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden',
            maxWidth: 800,
            margin: { xs: '8px', sm: '16px', md: '24px' },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          }
        }}
      >
        <Box display="flex" justifyContent="flex-end" p={1} position="absolute" right={0} zIndex={1}>
          <IconButton onClick={handleCloseDialog} sx={{ color: '#000', fontSize: "12px" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {selectedItem && (
          <DialogContent sx={{ p: 0 }}>
            <Box>
              <Typography
                variant="h6"
                fontSize={{ xs: 16, sm: 18 }}
                fontWeight={600}
                p={{ xs: 1.5, sm: 2 }}
                pb={{ xs: 0.5, sm: 1 }}
              >
                {selectedItem.Name}
              </Typography>

              {selectedItem.Description__c && (
                <Typography
                  variant="body2"
                  fontSize={{ xs: 12, sm: 14 }}
                  fontWeight={500}
                  color="text.secondary"
                  px={{ xs: 1.5, sm: 2 }}
                  pb={{ xs: 1.5, sm: 2 }}
                >
                  {selectedItem.Description__c}
                </Typography>
              )}

              {isValidImageUrl(selectedItem.File_Link__c) ? (
                <Box
                  component="img"
                  src={selectedItem.File_Link__c}
                  alt={selectedItem.Name}
                  sx={{
                    width: '100%',
                    minHeight: { xs: 200, sm: 250, md: 300 },
                    maxHeight: { xs: 300, sm: 400, md: 500 },
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 200, sm: 300, md: 400 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f0f0f0',
                  }}
                >
                  <ImageIcon
                    sx={{
                      fontSize: { xs: 60, sm: 100, md: 150 },
                      color: '#bdbdbd'
                    }}
                  />
                </Box>
              )}

              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                p={{ xs: 1.5, sm: 2 }}
                gap={{ xs: 1, sm: 0 }}
                sx={{ backgroundColor: '#f5f5f5' }}
              >
                {/* User Info */}
                <Box display="flex" alignItems="center">
                  <Avatar
                    src={selectedItem.File_Admin__r?.Profile_Picture_URL__c}
                    alt={selectedItem.Name}
                    sx={{
                      width: { xs: 30, sm: 36 },
                      height: { xs: 30, sm: 36 },
                      marginRight: 1
                    }}
                  />
                  <Typography
                    fontWeight={500}
                    color="#373A45"
                    fontSize={{ xs: 12, sm: 14 }}
                  >
                    {selectedItem.Name}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  {selectedItem.Location__c && (
                    <Box display="flex" alignItems="center">
                      <LocationOnIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: '#666' }} />
                      <Typography
                        variant="body2"
                        fontSize={{ xs: 12, sm: 14 }}
                        fontWeight={500}
                        color="text.secondary"
                      >
                        {selectedItem.Location__c}
                      </Typography>
                    </Box>
                  )}

                  <LocalOfferIcon
                    sx={{
                      fontSize: { xs: 12, sm: 14 },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
};

export default PhotoContainer;